<template>
  <div class="rich-text-editor">
    <div v-if="editor" class="editor-toolbar">
      <v-btn-group density="compact" variant="outlined">
        <v-btn
          size="small"
          :color="editor.isActive('bold') ? 'primary' : undefined"
          @click="editor.chain().focus().toggleBold().run()"
          title="Gras"
        >
          <v-icon>mdi-format-bold</v-icon>
        </v-btn>
        <v-btn
          size="small"
          :color="editor.isActive('italic') ? 'primary' : undefined"
          @click="editor.chain().focus().toggleItalic().run()"
          title="Italique"
        >
          <v-icon>mdi-format-italic</v-icon>
        </v-btn>
        <v-btn
          size="small"
          :color="editor.isActive('underline') ? 'primary' : undefined"
          @click="editor.chain().focus().toggleUnderline().run()"
          title="Souligné"
        >
          <v-icon>mdi-format-underline</v-icon>
        </v-btn>
      </v-btn-group>

      <v-btn-group density="compact" variant="outlined" class="ml-2">
        <v-btn
          size="small"
          :color="editor.isActive('bulletList') ? 'primary' : undefined"
          @click="editor.chain().focus().toggleBulletList().run()"
          title="Liste à puces"
        >
          <v-icon>mdi-format-list-bulleted</v-icon>
        </v-btn>
        <v-btn
          size="small"
          :color="editor.isActive('orderedList') ? 'primary' : undefined"
          @click="editor.chain().focus().toggleOrderedList().run()"
          title="Liste numérotée"
        >
          <v-icon>mdi-format-list-numbered</v-icon>
        </v-btn>
      </v-btn-group>

      <v-btn-group density="compact" variant="outlined" class="ml-2">
        <v-btn
          size="small"
          @click="editor.chain().focus().undo().run()"
          :disabled="!editor.can().undo()"
          title="Annuler"
        >
          <v-icon>mdi-undo</v-icon>
        </v-btn>
        <v-btn
          size="small"
          @click="editor.chain().focus().redo().run()"
          :disabled="!editor.can().redo()"
          title="Rétablir"
        >
          <v-icon>mdi-redo</v-icon>
        </v-btn>
      </v-btn-group>
    </div>

    <editor-content :editor="editor" class="editor-content" />
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { watch, onBeforeUnmount } from 'vue'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: 'Écrivez vos notes ici...'
  }
})

const emit = defineEmits(['update:modelValue'])

const editor = useEditor({
  extensions: [
    StarterKit.configure({
      heading: false, // désactive les titres pour simplifier
    }),
    Underline,
  ],
  content: props.modelValue,
  editorProps: {
    attributes: {
      class: 'editor-textarea',
    },
  },
  onUpdate: ({ editor }) => {
    emit('update:modelValue', editor.getHTML())
  },
})

watch(() => props.modelValue, (newValue) => {
  if (editor.value && editor.value.getHTML() !== newValue) {
    editor.value.commands.setContent(newValue, false)
  }
})

onBeforeUnmount(() => {
  if (editor.value) {
    editor.value.destroy()
  }
})
</script>

<style scoped>
.rich-text-editor {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 4px;
  overflow: hidden;
}

.editor-toolbar {
  padding: 8px;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background-color: rgba(var(--v-theme-surface), 0.5);
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.editor-content {
  padding: 12px;
  min-height: 150px;
  max-height: 400px;
  overflow-y: auto;
}

:deep(.editor-textarea) {
  outline: none;
  min-height: 120px;
}

:deep(.editor-textarea p) {
  margin: 0 0 0.5em 0;
}

:deep(.editor-textarea p:last-child) {
  margin-bottom: 0;
}

:deep(.editor-textarea ul),
:deep(.editor-textarea ol) {
  padding-left: 1.5em;
  margin: 0.5em 0;
}

:deep(.editor-textarea li) {
  margin: 0.25em 0;
}

:deep(.editor-textarea strong) {
  font-weight: bold;
}

:deep(.editor-textarea em) {
  font-style: italic;
}

:deep(.editor-textarea u) {
  text-decoration: underline;
}

:deep(.ProseMirror-focused) {
  outline: none;
}
</style>
